// One-time lift of the Offer Ladder from localStorage up to Supabase.
//
// WHY THIS IS SAFE TO RE-RUN
// Offers created in the browser already carry a client-generated id (`off_xxxxxxxx`).
// Migrating keeps that id, so the write is an upsert onto the same primary key —
// running twice updates the same row rather than creating a second one. That's the
// property that makes a partial failure recoverable instead of duplicating.
//
// WHY THE SEED FLAG MOVES IN THE SAME STEP
// `brill_offers_seeded_v1` in localStorage records that the once-ever pull from
// the Accelerator already happened. If the offers move to the cloud and that flag
// stays behind, the pull re-fires and creates a DUPLICATE Signature Offer. So the
// flag is carried up to `settings.offers_seeded` here, and the auto-pull must not
// run until this has been attempted — see the `enabled` gate in useOffers.

import { loadOffers } from "./offerStore";

const MIGRATED_KEY = "brill_offers_migrated_v1";
const LEGACY_SEED_KEY = "brill_offers_seeded_v1";

export function offerMigrationDone() {
  try { return localStorage.getItem(MIGRATED_KEY) === "1"; } catch { return true; }
}

function markMigrated() {
  try { localStorage.setItem(MIGRATED_KEY, "1"); } catch { /* ignore */ }
}

export function legacySeeded() {
  try { return localStorage.getItem(LEGACY_SEED_KEY) === "1"; } catch { return false; }
}

/**
 * Move any browser-stored offers into Supabase.
 *
 * @param save        recordStore's save — returns the saved object, or null on failure
 * @param markSeeded  async; persists settings.offers_seeded = true
 * @returns {{moved: number, failed: number, seeded: boolean}}
 */
export async function migrateOffersToSupabase({ save, markSeeded }) {
  const local = loadOffers();
  let moved = 0;
  let failed = 0;

  for (const offer of local) {
    // Strip the localStorage-era timestamps: they're epoch numbers, where the
    // columns are timestamptz with their own defaults and an update trigger.
    // Keeping the id is the whole point — see the note above.
    const { createdAt, updatedAt, ...rest } = offer;
    if (await save(rest)) moved++;
    else failed++;
  }

  /* Carry the flag up whenever the browser had it set, OR whenever we just moved a
     pulled offer — in both cases the pull has already happened and must not run
     again. Doing this BEFORE marking the migration complete means a failure here
     leaves the whole thing retryable. */
  let seeded = false;
  const pulledOne = local.some((o) => o.source === "accelerator");
  if ((legacySeeded() || pulledOne) && markSeeded) {
    await markSeeded();
    seeded = true;
  }

  // Only claim done when nothing failed, so a network blip mid-migration retries
  // on the next load rather than silently abandoning her remaining offers.
  if (failed === 0) markMigrated();

  return { moved, failed, seeded };
}
